import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from 'lucide-react';
import { Button } from './Button.tsx';
import { IconButton } from './IconButton.tsx';
import { Input } from './Input.tsx';
import { cx } from './cx.ts';

/**
 * Page-based pagination strip. Position readout is mono
 * (`current / total`); labels arrive translated from the caller.
 *
 * Two forms share one component (#1938). Omitting `full` keeps the original
 * compact prev/next strip, which is what every pre-existing call site renders.
 * Passing `full` opts into the long-list form: a row-count summary, first/last
 * jumps, numbered pages with ±`PAGE_SKIP` skip-ahead ellipses, and a
 * jump-to-page box. New long tables should take the second form; the compact
 * ones migrate by adding the prop, not by swapping components.
 */

/** How many pages a skip-ahead ellipsis moves. Matches the ± window below. */
const PAGE_SKIP = 5;
/** Numbered pages kept on each side of the current page. */
const PAGE_WINDOW = 2;

export interface PaginationFullOptions {
  /** Row-count summary, already translated (e.g. 显示第 1–50 条,共 320 条). */
  readonly summary: string;
  readonly firstLabel: string;
  readonly lastLabel: string;
  readonly skipPrevLabel: string;
  readonly skipNextLabel: string;
  readonly jumpAriaLabel: string;
  readonly jumpSubmitLabel: string;
  readonly pageLabel: (page: number) => string;
  /** Disables every control while a fetch is in flight. */
  readonly busy?: boolean;
}

interface Props {
  readonly page: number;
  readonly pageCount: number;
  readonly onPageChange: (page: number) => void;
  readonly prevLabel: string;
  readonly nextLabel: string;
  readonly ariaLabel: string;
  readonly className?: string;
  readonly testId?: string;
  readonly full?: PaginationFullOptions;
}

/**
 * The page numbers to render, with `null` standing for a skip-ahead ellipsis.
 * First and last are always present so the ends of the list stay one click
 * away no matter how deep the operator is.
 */
export function paginationPageItems(page: number, pageCount: number): (number | null)[] {
  const total = Math.max(pageCount, 1);
  const current = clampPage(page, total);
  const shown = new Set<number>([1, total]);
  for (let p = current - PAGE_WINDOW; p <= current + PAGE_WINDOW; p += 1) {
    if (p >= 1 && p <= total) shown.add(p);
  }
  const ordered = [...shown].sort((a, b) => a - b);
  const items: (number | null)[] = [];
  let previous = 0;
  for (const p of ordered) {
    if (previous !== 0 && p - previous > 1) items.push(null);
    items.push(p);
    previous = p;
  }
  return items;
}

function clampPage(page: number, pageCount: number): number {
  if (!Number.isFinite(page)) return 1;
  return Math.min(Math.max(Math.trunc(page), 1), Math.max(pageCount, 1));
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  prevLabel,
  nextLabel,
  ariaLabel,
  className,
  testId,
  full,
}: Props): JSX.Element {
  const total = Math.max(pageCount, 1);
  const disabled = full?.busy === true;
  const strip = (
    <>
      {full !== undefined ? (
        <IconButton
          size="sm"
          label={full.firstLabel}
          disabled={disabled || page <= 1}
          onClick={() => onPageChange(1)}
          data-testid={testId === undefined ? undefined : `${testId}-first`}
        >
          <ChevronsLeft aria-hidden="true" className="h-4 w-4" />
        </IconButton>
      ) : null}
      <IconButton
        size="sm"
        label={prevLabel}
        disabled={disabled || page <= 1}
        onClick={() => onPageChange(page - 1)}
        data-testid={testId === undefined ? undefined : `${testId}-prev`}
      >
        <ChevronLeft aria-hidden="true" className="h-4 w-4" />
      </IconButton>
      {full === undefined ? (
        <span className="font-mono text-label text-paper-mid">
          {page} / {total}
        </span>
      ) : (
        <PageNumbers
          page={page}
          pageCount={total}
          onPageChange={onPageChange}
          options={full}
          disabled={disabled}
          testId={testId}
        />
      )}
      <IconButton
        size="sm"
        label={nextLabel}
        disabled={disabled || page >= total}
        onClick={() => onPageChange(page + 1)}
        data-testid={testId === undefined ? undefined : `${testId}-next`}
      >
        <ChevronRight aria-hidden="true" className="h-4 w-4" />
      </IconButton>
      {full !== undefined ? (
        <IconButton
          size="sm"
          label={full.lastLabel}
          disabled={disabled || page >= total}
          onClick={() => onPageChange(total)}
          data-testid={testId === undefined ? undefined : `${testId}-last`}
        >
          <ChevronsRight aria-hidden="true" className="h-4 w-4" />
        </IconButton>
      ) : null}
    </>
  );

  if (full === undefined) {
    return (
      <nav
        aria-label={ariaLabel}
        data-testid={testId}
        className={cx('flex items-center gap-2', className)}
      >
        {strip}
      </nav>
    );
  }

  return (
    <nav
      aria-label={ariaLabel}
      data-testid={testId}
      className={cx('flex flex-wrap items-center justify-between gap-3', className)}
    >
      <span
        className="text-dense text-paper-low"
        data-testid={testId === undefined ? undefined : `${testId}-summary`}
      >
        {full.summary}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {strip}
        <span className="ml-1 font-mono text-label text-paper-low">
          {page} / {total}
        </span>
        <JumpBox
          pageCount={total}
          onPageChange={onPageChange}
          options={full}
          disabled={disabled}
          testId={testId}
        />
      </div>
    </nav>
  );
}

function PageNumbers({
  page,
  pageCount,
  onPageChange,
  options,
  disabled,
  testId,
}: {
  readonly page: number;
  readonly pageCount: number;
  readonly onPageChange: (page: number) => void;
  readonly options: PaginationFullOptions;
  readonly disabled: boolean;
  readonly testId: string | undefined;
}): JSX.Element {
  const items = paginationPageItems(page, pageCount);
  let gapIndex = 0;
  return (
    <>
      {items.map((item, index) => {
        if (item === null) {
          // An ellipsis is a control, not decoration: it skips a fixed block of
          // pages, which is the only way to cross a long list without typing a
          // number or clicking through every page.
          const skipsBack = index === 1;
          const target = clampPage(skipsBack ? page - PAGE_SKIP : page + PAGE_SKIP, pageCount);
          gapIndex += 1;
          return (
            <IconButton
              key={`gap-${gapIndex}`}
              size="sm"
              label={skipsBack ? options.skipPrevLabel : options.skipNextLabel}
              disabled={disabled}
              onClick={() => onPageChange(target)}
              data-testid={
                testId === undefined
                  ? undefined
                  : `${testId}-skip-${skipsBack ? 'prev' : 'next'}`
              }
            >
              <MoreHorizontal aria-hidden="true" className="h-4 w-4" />
            </IconButton>
          );
        }
        const active = item === page;
        return (
          <button
            key={item}
            type="button"
            disabled={disabled}
            aria-label={options.pageLabel(item)}
            aria-current={active ? 'page' : undefined}
            onClick={() => onPageChange(item)}
            data-testid={testId === undefined ? undefined : `${testId}-page-${item}`}
            className={cx(
              'h-7 min-w-7 rounded px-1.5 font-mono text-label transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 disabled:pointer-events-none disabled:opacity-50',
              active
                ? 'bg-raised text-paper'
                : 'text-paper-mid hover:bg-raised hover:text-paper',
            )}
          >
            {item}
          </button>
        );
      })}
    </>
  );
}

function JumpBox({
  pageCount,
  onPageChange,
  options,
  disabled,
  testId,
}: {
  readonly pageCount: number;
  readonly onPageChange: (page: number) => void;
  readonly options: PaginationFullOptions;
  readonly disabled: boolean;
  readonly testId: string | undefined;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  // A page count that shrinks under the operator (a filter narrowing the list)
  // leaves a stale draft that would now clamp somewhere unexpected; drop it.
  useEffect(() => setDraft(''), [pageCount]);

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const parsed = Number.parseInt(draft.trim(), 10);
    if (!Number.isFinite(parsed)) return;
    // Out-of-range input is clamped rather than refused: "page 9999" reads as
    // "the end", and an inline error here would be chrome nobody wants.
    onPageChange(clampPage(parsed, pageCount));
    setDraft('');
  }

  return (
    <form className="flex items-center gap-1.5" onSubmit={submit} noValidate>
      <Input
        type="text"
        inputMode="numeric"
        value={draft}
        mono
        onChange={(e) => setDraft(e.currentTarget.value)}
        aria-label={options.jumpAriaLabel}
        placeholder={options.jumpAriaLabel}
        disabled={disabled}
        data-testid={testId === undefined ? undefined : `${testId}-jump-input`}
        className="h-7 px-2 text-label"
        wrapperClassName="w-20"
      />
      <Button
        type="submit"
        size="sm"
        disabled={disabled || draft.trim().length === 0}
        data-testid={testId === undefined ? undefined : `${testId}-jump-submit`}
      >
        {options.jumpSubmitLabel}
      </Button>
    </form>
  );
}
